import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { Button } from './ui/button';
import { Calendar, MapPin, Building2, RefreshCw, Download, Search, Bell } from 'lucide-react';
import { useState } from 'react';

/**
 * Reusable Dashboard Filters Component
 * Provides Date Range, City, and Store filters with action buttons
 * City and Store dropdowns are searchable for large lists
 */
export function DashboardFilters({ 
  filters, 
  setFilters, 
  filterOptions = { cities: [], stores: [] },
  onRefresh,
  onExport,
  loading = false,
  showSearch = false,
  className = ""
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Convert cities to option format
  const cityOptions = (filterOptions.cities || []).map(city => ({
    value: city,
    label: city
  }));

  // Convert stores to option format
  const storeOptions = (filterOptions.stores || []).map(store => ({
    value: store.code || store.store_id || store.name,
    label: store.name || store.store_name || store.code
  }));

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-wrap items-center gap-3">
        {/* Date Range Filter */}
        <Select 
          value={filters.dateRange || 'lifetime'} 
          onValueChange={(v) => setFilters(prev => ({ ...prev, dateRange: v }))}
        >
          <SelectTrigger className="w-32" data-testid="filter-date-range">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="lifetime">Lifetime</SelectItem>
            <SelectItem value="ytd">YTD</SelectItem>
            <SelectItem value="12m">Last 12M</SelectItem>
            <SelectItem value="6m">Last 6M</SelectItem>
            <SelectItem value="3m">Last 3M</SelectItem>
          </SelectContent>
        </Select>

        {/* City Filter - Searchable */}
        <SearchableSelect
          value={filters.city || 'all'}
          onValueChange={(v) => setFilters(prev => ({ ...prev, city: v }))}
          options={cityOptions}
          placeholder="All Cities"
          searchPlaceholder="Search cities..."
          emptyText="No cities found."
          icon={MapPin}
          triggerClassName="w-40"
          data-testid="filter-city"
        />

        {/* Store Filter - Searchable */}
        <SearchableSelect
          value={filters.store || 'all'}
          onValueChange={(v) => setFilters(prev => ({ ...prev, store: v }))}
          options={storeOptions}
          placeholder="All Stores"
          searchPlaceholder="Search stores..."
          emptyText="No stores found."
          icon={Building2}
          triggerClassName="w-48"
          data-testid="filter-store"
        />

        {/* Action Buttons */}
        <div className="flex items-center gap-2 ml-auto">
          {showSearch && (
            <Button 
              variant="outline" 
              size="icon" 
              onClick={() => setSearchOpen(!searchOpen)}
              data-testid="search-btn"
            >
              <Search className="w-4 h-4" />
            </Button>
          )}
          
          {onRefresh && (
            <Button 
              variant="outline" 
              size="icon" 
              onClick={onRefresh}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          )}
          
          {onExport && (
            <Button 
              variant="outline" 
              size="icon" 
              onClick={onExport}
              data-testid="download-btn"
            >
              <Download className="w-4 h-4" />
            </Button>
          )}
          
          <Button 
            variant="outline" 
            size="icon"
            className="relative"
            data-testid="notifications-btn"
          >
            <Bell className="w-4 h-4" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
          </Button>
        </div>
      </div>

      {/* Search Bar (conditional) */}
      {searchOpen && (
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search stores, metrics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-muted/50 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
          </div>
          <Button variant="ghost" size="sm" onClick={() => { setSearchOpen(false); setSearchQuery(''); }}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

export default DashboardFilters;
